import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Activity,
  ArrowLeft,
  Bot,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  OctagonAlert,
  Play,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  getAutomationCentre,
  retryEvent,
  runAgentsNow,
  setAgentMode,
  setEmergencyStop,
} from "@/lib/agents.functions";
import { Button } from "@/components/ui/button";
import { OwnerAssistant } from "@/components/owner/OwnerAssistant";
import { ukDateTime } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/owner/automation")({
  head: () => ({
    meta: [
      { title: "Shields Command Centre" },
      { name: "description", content: "Supervise Shields agents, approvals and operations." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AutomationCentre,
});

const MODES = ["draft", "active", "paused", "disabled"] as const;
const MODE_STYLE: Record<string, string> = {
  draft: "bg-gold/15 text-foreground",
  active: "bg-success/12 text-success",
  paused: "bg-muted text-foreground",
  disabled: "bg-destructive/12 text-destructive",
};

function AutomationCentre() {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["owner-automation"],
    queryFn: () => getAutomationCentre(),
    retry: false,
  });

  async function act(
    run: () => Promise<{ ok?: boolean; error?: string } | unknown>,
    success: string,
  ) {
    setBusy(true);
    try {
      const result = (await run()) as { ok?: boolean; error?: string };
      if (result && result.ok === false) toast.error(result.error ?? "That didn't work");
      else toast.success(success);
      await queryClient.invalidateQueries({ queryKey: ["owner-automation"] });
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : "That didn't work");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-4 px-5 py-8">
        <div className="h-28 animate-pulse rounded-3xl bg-muted" />
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="h-96 animate-pulse rounded-3xl bg-muted" />
          <div className="h-96 animate-pulse rounded-3xl bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center">
        <p className="text-lg font-semibold">We couldn't load the Command Centre.</p>
        <Button className="mt-4" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const executableAgents = data.agents.filter((agent) => agent["executable"]);
  const activeCount = executableAgents.filter((agent) => agent["mode"] === "active").length;
  const successfulRuns = data.runs.filter((run) => run["outcome"] === "success").length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex min-h-16 max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-3 sm:px-8">
          <div className="flex items-center gap-3">
            <Link
              to="/owner"
              aria-label="Back to owner dashboard"
              className="flex h-10 w-10 items-center justify-center rounded-xl hover:bg-accent"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </Link>
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <ShieldCheck className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <h1 className="font-display text-lg font-extrabold leading-tight">
                Shields Command Centre
              </h1>
              <p className="text-xs text-muted-foreground">Owner-only · every action is recorded</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-extrabold ${data.emergencyStopped ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"}`}
            >
              <span
                className={`h-2 w-2 rounded-full ${data.emergencyStopped ? "bg-destructive" : "bg-success"}`}
              />
              {data.emergencyStopped ? "Automation stopped" : "Systems supervised"}
            </span>
            <Link
              to="/owner/approvals"
              className="rounded-xl border border-border px-3 py-2 text-xs font-bold hover:bg-accent"
            >
              {data.approvals.length} approvals
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-5 py-7 sm:px-8">
        <section
          className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
          aria-label="Automation summary"
        >
          <Metric
            icon={Bot}
            label="Active agents"
            value={`${activeCount}/${executableAgents.length}`}
            detail={`${data.agents.length - executableAgents.length} departments remain planning-only`}
          />
          <Metric
            icon={Activity}
            label="Queue"
            value={String(data.queueDepth)}
            detail="Events waiting to be handled"
          />
          <Metric
            icon={CheckCircle2}
            label="Recent runs"
            value={String(successfulRuns)}
            detail={`of ${data.runs.length} completed successfully`}
          />
          <Metric
            icon={CircleDollarSign}
            label="AI spend today"
            value={`£${(data.costTodayPence / 100).toFixed(2)}`}
            detail={`${data.tokensToday.toLocaleString("en-GB")} tokens`}
          />
        </section>

        <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(22rem,0.85fr)]">
          <div className="space-y-6">
            <div className="overflow-hidden rounded-3xl bg-navy p-6 text-navy-foreground shadow-[var(--shadow-premium)] sm:p-8">
              <div className="flex flex-wrap items-start justify-between gap-5">
                <div className="max-w-xl">
                  <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-navy-foreground/60">
                    Supervised automation
                  </p>
                  <h2 className="mt-2 text-2xl font-extrabold">
                    Shields handles the routine. You control the risk.
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-navy-foreground/70">
                    The orchestrator routes work to specialist agents. Payments, credit grants,
                    legal changes and other high-impact actions wait for your approval.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    disabled={busy || data.emergencyStopped}
                    onClick={() => void act(() => runAgentsNow(), "Orchestrator run complete")}
                  >
                    <Play className="mr-2 h-4 w-4" /> Run now
                  </Button>
                  <Button
                    variant={data.emergencyStopped ? "secondary" : "destructive"}
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => setEmergencyStop({ data: { paused: !data.emergencyStopped } }),
                        data.emergencyStopped ? "Automation resumed" : "All new agent work stopped",
                      )
                    }
                  >
                    <OctagonAlert className="mr-2 h-4 w-4" />
                    {data.emergencyStopped ? "Resume" : "Emergency stop"}
                  </Button>
                </div>
              </div>
              <p className="mt-5 border-t border-white/10 pt-4 text-xs text-navy-foreground/55">
                Emergency stop blocks new agent work. Customer bookings and messages remain
                available.
              </p>
            </div>

            <section>
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    Agent network
                  </p>
                  <h2 className="mt-1 text-xl font-extrabold">Specialists</h2>
                </div>
                <span className="text-xs text-muted-foreground">Draft agents only propose</span>
              </div>
              <div className="space-y-3">
                {data.agents.map((agent) => (
                  <article key={agent["key"]} className="card-surface p-5 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-soft text-primary">
                            <Bot className="h-4 w-4" />
                          </span>
                          <h3 className="font-extrabold">{agent["name"]}</h3>
                          <span
                            className={`rounded-full px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wide ${MODE_STYLE[agent["mode"]] ?? "bg-muted"}`}
                          >
                            {agent["mode"] === "draft" ? "draft only" : agent["mode"]}
                          </span>
                          {!agent["executable"] && (
                            <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-bold text-muted-foreground">
                              Planning hub
                            </span>
                          )}
                        </div>
                        <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
                          {agent["description"]}
                        </p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          Limit: {agent["daily_action_limit"]} actions · £
                          {(agent["daily_cost_limit_pence"] / 100).toFixed(2)} per day
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {MODES.map((mode) => (
                          <Button
                            key={mode}
                            size="sm"
                            variant={agent["mode"] === mode ? "default" : "outline"}
                            disabled={
                              busy ||
                              agent["mode"] === mode ||
                              (mode === "active" && !agent["executable"])
                            }
                            onClick={() =>
                              void act(
                                () => setAgentMode({ data: { key: agent["key"], mode } }),
                                `${agent["name"]} set to ${mode}`,
                              )
                            }
                          >
                            {mode}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>

          <div className="space-y-6 lg:sticky lg:top-6">
            <OwnerAssistant onActed={() => void refetch()} />
            <Link
              to="/owner/approvals"
              className="card-surface group flex items-center gap-4 p-5 shadow-sm"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/15 text-foreground">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-extrabold">Approval inbox</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Review evidence before high-impact work is allowed.
                </p>
              </div>
              <span className="rounded-full bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground">
                {data.approvals.length}
              </span>
              <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div>
            <div className="mb-3">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                Exceptions
              </p>
              <h2 className="mt-1 text-xl font-extrabold">Failed tasks</h2>
            </div>
            {data.failedEvents.length === 0 ? (
              <p className="card-surface p-5 text-sm text-muted-foreground">
                No failures in the queue.
              </p>
            ) : (
              <div className="space-y-3">
                {data.failedEvents.map((event) => (
                  <div
                    key={event["id"]}
                    className="card-surface flex flex-wrap items-center justify-between gap-3 p-4"
                  >
                    <div>
                      <p className="font-bold">{event["event_type"]}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {event["retry_count"]} retries · {event["error"] ?? "Unknown error"}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        void act(
                          () => retryEvent({ data: { eventId: event["id"] } }),
                          "Queued for retry",
                        )
                      }
                    >
                      <RefreshCw className="mr-2 h-4 w-4" /> Retry
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="mb-3">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                Audit trail
              </p>
              <h2 className="mt-1 text-xl font-extrabold">Recent agent actions</h2>
            </div>
            {data.tasks.length === 0 ? (
              <p className="card-surface p-5 text-sm text-muted-foreground">
                No agent activity yet.
              </p>
            ) : (
              <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
                {data.tasks.slice(0, 10).map((task) => (
                  <li key={task["id"]} className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-bold">
                        {task["agent_key"]} · {task["action"]}
                      </p>
                      <span className="rounded-full bg-muted px-2 py-1 text-[10px] font-bold uppercase">
                        {task["status"]}
                      </span>
                    </div>
                    {task["rationale"] ? (
                      <p className="mt-1 text-sm text-muted-foreground">{task["rationale"]}</p>
                    ) : null}
                    {task["error"] ? (
                      <p className="mt-1 text-sm text-destructive">{task["error"]}</p>
                    ) : null}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {ukDateTime(task["created_at"])}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: typeof Bot;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <article className="card-surface p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-muted-foreground">{label}</p>
        <Icon className="h-5 w-5 text-primary" aria-hidden />
      </div>
      <p className="mt-3 text-2xl font-extrabold">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
    </article>
  );
}
